import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { FormulaTooltip } from '../FormulaTooltip';
import { 
  BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import { 
  Store,
  Trophy,
  Medal,
  Award,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Sparkles,
  MapPin,
  Calendar,
  Flame,
  Target,
  Zap,
  ArrowUp,
  ArrowDown,
  Minus,
  Eye,
  X,
  BarChart3,
  PieChart,
  Download,
  ZoomIn,
  Clock
} from 'lucide-react';
import axios from 'axios';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';
import DashboardFilters from '../DashboardFilters';
import { ExportButton } from '../ExportUtils';
import { AskAIButton } from '../AskAIButton';

import { API_BASE_URL as API } from '../../utils/apiConfig';

// Health Score Gauge Component
const HealthGauge = ({ score, size = 120 }) => {
  const radius = size / 2 - 10;
  const circumference = radius * Math.PI;
  const progress = (score / 100) * circumference;
  
  const getColor = (s) => {
    if (s >= 80) return '#10B981';
    if (s >= 60) return '#3B82F6';
    if (s >= 40) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div className="relative" style={{ width: size, height: size / 2 + 20 }}>
      <svg width={size} height={size / 2 + 10} className="transform -rotate-0">
        <path
          d={`M ${10} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 10} ${size / 2}`}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <path
          d={`M ${10} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 10} ${size / 2}`}
          fill="none"
          stroke={getColor(score)}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
        <span className="text-2xl font-bold" style={{ color: getColor(score) }}>{score}</span>
        <span className="text-xs text-muted-foreground">Health Score</span>
      </div>
    </div>
  );
};

// Tier Badge Component
const TierBadge = ({ tier }) => {
  const icons = { Gold: Trophy, Silver: Medal, Bronze: Award, Standard: Store };
  const Icon = icons[tier.name] || Store;
  
  return (
    <div 
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
    >
      <Icon className="w-3.5 h-3.5" />
      {tier.name}
    </div>
  );
};

// Store Leaderboard Card - Innovative Design
const LeaderboardCard = ({ store, isExpanded, onToggle, onCompare, isComparing }) => {
  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(0)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border-yellow-500/30";
    if (rank === 2) return "bg-gradient-to-r from-slate-400/20 to-gray-400/10 border-slate-400/30";
    if (rank === 3) return "bg-gradient-to-r from-orange-600/20 to-amber-600/10 border-orange-600/30";
    return "bg-card/50 border-border";
  };

  const getSsgIcon = (ssg) => {
    if (ssg > 5) return <ArrowUp className="w-3.5 h-3.5 text-emerald-400" />;
    if (ssg < -5) return <ArrowDown className="w-3.5 h-3.5 text-red-400" />;
    return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  return (
    <Card 
      className={`transition-all duration-300 border ${getRankStyle(store.rank)} ${isComparing ? 'ring-2 ring-primary' : ''}`}
    >
      <CardContent className="p-0">
        {/* Main Row */}
        <div 
          className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={onToggle}
        >
          {/* Rank */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg ${
            store.rank <= 3 ? 'bg-gradient-to-br from-primary to-violet-600 text-white' : 'bg-muted text-muted-foreground'
          }`}>
            {store.rank}
          </div>
          
          {/* Store Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate">{store.store_name}</h3>
              <TierBadge tier={store.tier} />
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{formatCurrency(store.revenue)}</span>
              <span>•</span>
              <span>{store.customers?.toLocaleString()} customers</span>
              <span>•</span>
              <span>ATV ₹{store.atv?.toLocaleString()}</span>
            </div>
          </div>
          
          {/* Metrics Pills */}
          <div className="hidden md:flex items-center gap-2">
            <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-medium">
              {store.repeat_pct}% repeat
            </div>
            <div className="px-2.5 py-1 rounded-lg bg-violet-500/10 text-violet-400 text-xs font-medium">
              {store.burn_pct}% burn
            </div>
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-muted text-xs font-medium">
              {getSsgIcon(store.ssg)}
              {store.ssg > 0 ? '+' : ''}{store.ssg}% SSG
            </div>
          </div>
          
          {/* Health Score */}
          <HealthGauge score={store.health_score} size={80} />
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className={`h-8 w-8 p-0 ${isComparing ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={(e) => { e.stopPropagation(); onCompare(store); }}
            >
              <BarChart3 className="w-4 h-4" />
            </Button>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-2 border-t border-border/50 bg-muted/20">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="p-3 rounded-lg bg-background/50">
                <FormulaTooltip name="Revenue Share" formula="store_revenue ÷ brand_total_revenue × 100" source="Live aggregation">
                  <p className="text-xs text-muted-foreground mb-1">Revenue Share</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{store.revenue_pct}%</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50">
                <FormulaTooltip metric="total_transactions">
                  <p className="text-xs text-muted-foreground mb-1">Total Bills</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{store.bills?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50">
                <FormulaTooltip metric="repeat_customers">
                  <p className="text-xs text-muted-foreground mb-1">Repeat Customers</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{store.repeat_customers?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50">
                <FormulaTooltip metric="total_points_earned">
                  <p className="text-xs text-muted-foreground mb-1">Points Issued</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{(store.points_issued / 1000).toFixed(0)}K</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50">
                <FormulaTooltip metric="total_points_redeemed">
                  <p className="text-xs text-muted-foreground mb-1">Points Redeemed</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{(store.points_redeemed / 1000).toFixed(0)}K</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50">
                <FormulaTooltip name="Loyalty Share" formula="loyalty_bills ÷ total_bills × 100" source="Live aggregation">
                  <p className="text-xs text-muted-foreground mb-1">Loyalty Share</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{store.loyalty_share}%</p>
              </div>
            </div>
            
            {/* Outlier Badges */}
            {store.outliers?.length > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-muted-foreground">Highlights:</span>
                {store.outliers.map((o, i) => (
                  <Badge key={i} variant="outline" className={`text-${o.color}-400 border-${o.color}-400/30`}>
                    {o.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Store Comparison Modal
const ComparisonView = ({ stores, onClose }) => {
  if (stores.length < 2) return null;
  
  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(0)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  // Prepare radar chart data
  const radarData = [
    { metric: 'Revenue', ...Object.fromEntries(stores.map(s => [s.store_name.slice(0, 15), (s.revenue / stores[0].revenue) * 100])) },
    { metric: 'Customers', ...Object.fromEntries(stores.map(s => [s.store_name.slice(0, 15), (s.customers / Math.max(...stores.map(st => st.customers))) * 100])) },
    { metric: 'ATV', ...Object.fromEntries(stores.map(s => [s.store_name.slice(0, 15), (s.atv / Math.max(...stores.map(st => st.atv))) * 100])) },
    { metric: 'Repeat %', ...Object.fromEntries(stores.map(s => [s.store_name.slice(0, 15), s.repeat_pct * 2])) },
    { metric: 'Burn %', ...Object.fromEntries(stores.map(s => [s.store_name.slice(0, 15), s.burn_pct * 3])) },
    { metric: 'Health', ...Object.fromEntries(stores.map(s => [s.store_name.slice(0, 15), s.health_score])) },
  ];

  const colors = ['#8B5CF6', '#EC4899', '#10B981', '#F59E0B'];

  return (
    <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-background">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" />
          Store Comparison ({stores.length} stores)
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Radar Chart */}
          <div>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis tick={{ fontSize: 10 }} />
                {stores.map((store, idx) => (
                  <Radar
                    key={store.store_code}
                    name={store.store_name.slice(0, 15)}
                    dataKey={store.store_name.slice(0, 15)}
                    stroke={colors[idx % colors.length]}
                    fill={colors[idx % colors.length]}
                    fillOpacity={0.2}
                  />
                ))}
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Metric</th>
                  {stores.map((s, i) => (
                    <th key={i} className="text-right py-2 px-3" style={{ color: colors[i % colors.length] }}>
                      {s.store_name.slice(0, 12)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Revenue', key: 'revenue', format: formatCurrency },
                  { label: 'Customers', key: 'customers', format: v => v?.toLocaleString() },
                  { label: 'ATV', key: 'atv', format: v => `₹${v?.toLocaleString()}` },
                  { label: 'Repeat %', key: 'repeat_pct', format: v => `${v}%` },
                  { label: 'Burn %', key: 'burn_pct', format: v => `${v}%` },
                  { label: 'Health Score', key: 'health_score', format: v => v },
                  { label: 'SSG', key: 'ssg', format: v => `${v > 0 ? '+' : ''}${v}%` },
                ].map(row => (
                  <tr key={row.key} className="border-b border-border/50">
                    <td className="py-2 px-3 text-muted-foreground">{row.label}</td>
                    {stores.map((s, i) => (
                      <td key={i} className="text-right py-2 px-3 font-medium">
                        {row.format(s[row.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// City Card Component
const CityCard = ({ city, onClick }) => {
  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(0)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all group"
      onClick={() => onClick(city)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <MapPin className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold">{city.city}</h4>
              <p className="text-xs text-muted-foreground">{city.store_count} stores</p>
            </div>
          </div>
          <span className="text-xs px-2 py-1 rounded-full bg-muted">{city.revenue_pct}%</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="p-2 rounded bg-muted/50">
            <p className="text-lg font-bold">{formatCurrency(city.revenue)}</p>
            <p className="text-xs text-muted-foreground">Revenue</p>
          </div>
          <div className="p-2 rounded bg-muted/50">
            <p className="text-lg font-bold">{formatCurrency(city.avg_store_revenue)}</p>
            <p className="text-xs text-muted-foreground">Avg/Store</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity" />
      </CardContent>
    </Card>
  );
};

export default function StorePerformanceDashboard() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStore, setExpandedStore] = useState(null);
  const [comparisonStores, setComparisonStores] = useState([]);
  const [viewMode, setViewMode] = useState('leaderboard'); // leaderboard, cities, weekday
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [isLiveData, setIsLiveData] = useState(false);
  const [filters, setFilters] = useState({ dateRange: 'lifetime', city: 'all', store: 'all' });
  const [filterOptions, setFilterOptions] = useState({ cities: [], stores: [] });
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });

  // Chart zoom handlers
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({ open: true, type: chartType, data: chartData, title: chartTitle, config: chartConfig });
  };
  const closeChartZoom = () => setChartZoom({ open: false, type: '', data: [], title: '', config: {} });

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    setError(null);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    // Live status set after response
    // Add cache-busting to ensure fresh data
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Use original API for proper data structure
    try {
      const response = await axios.post(
        `${API}/api/analytics/store-performance?_t=${Date.now()}&_rid=${requestId}`,
        { brand_id: selectedBrand.brand_id, filters },
        { 
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'X-Request-ID': requestId
          }
        }
      );
      
      // Force new object reference
      setData({...response.data.data});
      setIsLiveData(response.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      
      // Extract filter options from response
      if (response.data.data?.filter_context) {
        setFilterOptions({
          cities: response.data.data.filter_context.cities || [],
          stores: response.data.data.filter_context.stores || []
        });
      } else if (response.data.data?.filterOptions) {
        setFilterOptions({
          cities: response.data.data.filterOptions.cities || [],
          stores: response.data.data.stores?.map(s => ({ code: s.store_code, name: s.store_name })) || []
        });
      }
    } catch (err) {
      console.error('Store performance data error:', err);
      setError('Failed to load store performance data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, getDbBrandId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    if (!data) return;
    
    // Helper to format CSV values properly
    const formatCsvValue = (val) => {
      if (val === null || val === undefined) return '';
      // If value contains comma, quote it
      const strVal = String(val);
      if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
        return `"${strVal.replace(/"/g, '""')}"`;
      }
      return strVal;
    };
    
    const csvContent = [
      ['Store', 'City', 'Revenue (₹)', 'Customers', 'ATV (₹)', 'Repeat %', 'Health Score'],
      ...(data.stores || []).map(s => [
        formatCsvValue(s.store_name), 
        formatCsvValue(s.city), 
        Math.round(s.revenue || 0), 
        s.customers || 0, 
        Math.round(s.atv || 0), 
        (s.repeat_pct || 0).toFixed(1), 
        Math.round(s.health_score || 0)
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `store_performance_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const toggleExpand = (storeCode) => {
    setExpandedStore(expandedStore === storeCode ? null : storeCode);
  };

  const toggleCompare = (store) => {
    setComparisonStores(prev => {
      const exists = prev.find(s => s.store_code === store.store_code);
      if (exists) {
        return prev.filter(s => s.store_code !== store.store_code);
      }
      if (prev.length >= 4) {
        return [...prev.slice(1), store];
      }
      return [...prev, store];
    });
  };

  const openDrillDown = (type, value, title, context = {}) => {
    setDrillDown({ open: true, type, data: { value, context }, title });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="store-performance-loading">
        <div className="flex flex-col items-center gap-4">
          <Store className="w-12 h-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Loading store performance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const stores = data?.stores || [];
  const cities = data?.cities || [];
  const tierDistribution = data?.tier_distribution || [];
  const weekdayPerformance = data?.weekday_performance || [];
  const insights = data?.insights || {};

  return (
    <div className="space-y-6" data-testid="store-performance-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
              <Store className="w-6 h-6 text-white" />
            </div>
            Store Performance Leaderboard
            {isLiveData && (
              <Badge className="ml-2 bg-emerald-500 text-white animate-pulse">
                <Zap className="w-3 h-3 mr-1" />
                LIVE
              </Badge>
            )}
            {data?.data_as_of && (
              <Badge className="ml-2 bg-blue-500/80 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Data as of: {data.data_as_of}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground mt-1">
            {isLiveData 
              ? `Live data: ${stores.length} stores analyzed from ${selectedBrand?.name || 'Brand'}`
              : 'Rank, compare, and optimize store performance'
            }
          </p>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center bg-muted rounded-lg p-1">
          <Button 
            variant={viewMode === 'leaderboard' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('leaderboard')}
            className="gap-2"
          >
            <Trophy className="w-4 h-4" />
            Leaderboard
          </Button>
          <Button 
            variant={viewMode === 'cities' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('cities')}
            className="gap-2"
          >
            <MapPin className="w-4 h-4" />
            By City
          </Button>
          <Button 
            variant={viewMode === 'weekday' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('weekday')}
            className="gap-2"
          >
            <Calendar className="w-4 h-4" />
            Day Analysis
          </Button>
        </div>
      </div>

      {/* Dashboard Filters */}
      <DashboardFilters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        onRefresh={fetchData}
        onExport={handleExport}
        loading={loading}
      />
      
      {/* Full Report Export Button */}
      <div className="flex justify-end mb-4">
        <ExportButton 
          brandId={selectedBrand?.brand_id}
          dashboardType="store_performance"
          label="Download All Stores"
          filename={`all_stores_${selectedBrand?.name || 'export'}.csv`}
        />
      </div>

      {/* Summary Stats Row - Unique Horizontal Scroll Design */}
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2">
        {[
          { label: 'Total Stores', value: summary.total_stores, icon: Store, color: '#8B5CF6' },
          { label: 'Network Revenue', value: formatCurrency(summary.total_revenue), icon: DollarSign, color: '#10B981' },
          { label: 'Avg Health Score', value: summary.avg_health_score, icon: Target, color: '#3B82F6', suffix: '/100' },
          { label: 'Gold Stores', value: summary.gold_stores, icon: Trophy, color: '#F59E0B' },
          { label: 'Avg Repeat %', value: `${summary.avg_repeat_pct || 42}%`, icon: Users, color: '#EC4899' },
          { label: 'Weekend Share', value: `${summary.weekend_share || 35}%`, icon: Calendar, color: '#14B8A6' },
        ].map((stat, idx) => (
          <Card 
            key={idx} 
            className="min-w-[180px] cursor-pointer hover:shadow-lg transition-all"
            onClick={() => openDrillDown('kpi', stat.label.toLowerCase().replace(/\s+/g, '_'), stat.label)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${stat.color}20` }}>
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-xl font-bold">{stat.value}{stat.suffix || ''}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tier Distribution - Unique Visual */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex h-3">
            {tierDistribution.map((tier, idx) => (
              <div 
                key={idx}
                className="relative group cursor-pointer transition-all hover:h-4"
                style={{ 
                  width: `${(tier.count / summary.total_stores) * 100}%`,
                  backgroundColor: tier.color
                }}
                onClick={() => openDrillDown('tier', tier.tier, `${tier.tier} Tier Stores`)}
              >
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-popover border rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                  {tier.tier}: {tier.count} stores ({((tier.count / summary.total_stores) * 100).toFixed(0)}%)
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between px-4 py-2 text-xs text-muted-foreground">
            {tierDistribution.map((tier, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tier.color }} />
                {tier.tier} ({tier.count})
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Comparison View */}
      {comparisonStores.length >= 2 && (
        <ComparisonView 
          stores={comparisonStores} 
          onClose={() => setComparisonStores([])} 
        />
      )}

      {/* Main Content based on View Mode */}
      {viewMode === 'leaderboard' && (
        <div className="space-y-3">
          {/* AI Insight Banner */}
          <Card className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-red-500/10 border-amber-500/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{insights.top_performer?.title}</h4>
                  <p className="text-xs text-muted-foreground">{insights.top_performer?.insight}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Leaderboard */}
          {stores.map(store => (
            <LeaderboardCard
              key={store.code || store.store_code}
              store={store}
              isExpanded={expandedStore === (store.code || store.store_code)}
              onToggle={() => toggleExpand(store.code || store.store_code)}
              onCompare={toggleCompare}
              isComparing={comparisonStores.some(s => (s.code || s.store_code) === (store.code || store.store_code))}
            />
          ))}
        </div>
      )}

      {viewMode === 'cities' && (
        <div className="space-y-6">
          {/* Cities Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cities.map(city => (
              <CityCard 
                key={city.city} 
                city={city} 
                onClick={(c) => openDrillDown('city', c.city, `${c.city} Performance`)}
              />
            ))}
          </div>

          {/* City Insight */}
          <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <MapPin className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Geographic Distribution</h4>
                  <p className="text-xs text-muted-foreground">
                    {cities[0]?.city} leads with {cities[0]?.store_count} stores generating {formatCurrency(cities[0]?.revenue)} 
                    ({cities[0]?.revenue_pct}% of network revenue).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {viewMode === 'weekday' && (
        <div className="space-y-6">
          {/* Weekday Performance Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Day-wise Performance Pattern
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weekdayPerformance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="revenue" radius={[8, 8, 0, 0]}>
                    {weekdayPerformance.map((entry, idx) => (
                      <Cell 
                        key={idx} 
                        fill={entry.is_weekend ? '#F59E0B' : '#3B82F6'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span>Weekdays</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span>Weekends</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weekday Insight */}
          <Card className="bg-gradient-to-r from-violet-500/10 to-pink-500/10 border-violet-500/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <Sparkles className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{insights.weekday_weekend?.title}</h4>
                  <p className="text-xs text-muted-foreground">{insights.weekday_weekend?.insight}</p>
                  <p className="text-xs text-foreground/80 mt-1 bg-background/30 p-2 rounded">
                    <strong>Recommendation:</strong> {insights.weekday_weekend?.recommendation}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bottom Insights Row */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20 cursor-pointer hover:shadow-lg"
              onClick={() => openDrillDown('insight', 'loyalty_leaders', 'Loyalty Leaders')}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Trophy className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{insights.loyalty_leaders?.title}</h4>
                <p className="text-xs text-muted-foreground">{insights.loyalty_leaders?.insight}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20 cursor-pointer hover:shadow-lg"
              onClick={() => openDrillDown('insight', 'improvement_needed', 'Stores Needing Attention')}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{insights.improvement_needed?.title}</h4>
                <p className="text-xs text-muted-foreground">{insights.improvement_needed?.insight}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deep AI Insights */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="store_performance"
        dashboardData={data}
        kpis={{
          total_customers: { value: data?.totals?.total_customers || 0 },
          total_revenue: { value: data?.totals?.total_revenue || 0 }
        }}
        stores={data?.stores || []}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="store_performance"
      />
    </div>
  );
}
